import React, { useEffect, useRef, useState } from 'react';
import { IoCloseOutline } from 'react-icons/io5';
import { BsPlusLg as PlusLg, BsXLg as XLg } from 'react-icons/bs';

import BayTooltip from '../BayTooltip';

import './BayTags.scss';

export interface TagItem {
	tagName: string;
	style?: string;
	[key: string]: any;
}

export interface BayTagsProps {
	data: TagItem[];
	removable?: boolean;
	allowHiddenTags?: boolean;
	maxVisibleTagWidth?: number;
	onDelete?: ((tag: TagItem) => void) | null;
	contactsTooltip?: boolean;
	setContactPublicId?: ((publicId: string) => void) | null;
	contactName?: string | null;
	setEnableAddTagsModal?: ((enable: boolean) => void) | null;
	enableAddTagsModal?: boolean;
	publicid?: string | null;
	ownerpublicid?: string | null;
	setTagsContactId?: ((id: string) => void) | null;
	tagContactId?: string | null;
	setIsLoadingTags?: ((loading: boolean) => void) | null;
	smallTagGroup?: boolean;
	setContactPublicInfo?: ((info: { publicid: string; ownerpublicid: string }) => void) | null;
	isLastColumn?: boolean;
	readOnly?: boolean;
}

const BayTags: React.FC<BayTagsProps> = ({
	data,
	removable,
	allowHiddenTags = false,
	maxVisibleTagWidth = 25,
	onDelete = null,
	contactsTooltip = false,
	setContactPublicId = null,
	contactName = null,
	setEnableAddTagsModal = null,
	enableAddTagsModal = false,
	publicid = null,
	ownerpublicid = null,
	setTagsContactId = null,
	tagContactId = null,
	setIsLoadingTags = null,
	smallTagGroup = false,
	setContactPublicInfo = null,
	isLastColumn = false,
	readOnly = false,
}) => {
	const [visibleTags, setVisibleTags] = useState<TagItem[]>([]);
	const [hiddenTags, setHiddenTags] = useState<TagItem[]>([]);
	const [longVisibleTags, setLongVisibleTags] = useState<TagItem[]>([]);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const modalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				modalRef.current &&
				!modalRef.current.contains(event.target as Node) &&
				!(event.target as Element).closest('[data-button-id="hiddenTagsButton"]')
			) {
				setIsModalVisible(false);
			}
		};
		const handleCloseOtherModals = () => setIsModalVisible(false);
		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('closeOtherModals', handleCloseOtherModals);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('closeOtherModals', handleCloseOtherModals);
		};
	}, []);

	const handleHiddenTagsClick = () => {
		if (!isModalVisible) {
			document.dispatchEvent(new CustomEvent('closeOtherModals'));
			setIsModalVisible((prev) => !prev);
		}
	};

	const calculateModalWidth = () => {
		const baseWidth = 300;
		const additionalWidthPerTag = 20;
		const maxModalWidth = 600;
		return Math.min(
			baseWidth + hiddenTags.length * additionalWidthPerTag,
			maxModalWidth,
		);
	};

	const getAddTagsButton = (showLabel = false) => {
		return (
			<div
				className="cursor-pointer"
				onClick={() => {
					setContactPublicId && publicid && setContactPublicId(publicid);
					setContactPublicInfo &&
						publicid &&
						ownerpublicid &&
						setContactPublicInfo({ publicid, ownerpublicid });
					setIsLoadingTags && setIsLoadingTags(true);
					setTagsContactId && tagContactId && setTagsContactId(tagContactId);
					setEnableAddTagsModal && setEnableAddTagsModal(!enableAddTagsModal);
				}}
			>
				{showLabel ? <span><PlusLg size={16} fill="var(--bs-gray-500)" /> Add Tags</span> :
					<div className="hidden-tag-badge border border-blue-100 bg-blue-50">
						<PlusLg size={16} fill="var(--bs-blue)" />
					</div>}
			</div>
		);
	};

	useEffect(() => {
		let totalTagsLength = 0;
		let maxTagLimit: TagItem[] = [];

		if (allowHiddenTags && data?.length > 1) {
			data?.forEach((tag) => {
				totalTagsLength += tag.tagName?.length || 0;

				if (totalTagsLength < maxVisibleTagWidth) {
					maxTagLimit = [...maxTagLimit, tag];
				} else {
					setHiddenTags((prevTags) => [...prevTags, tag]);
					setLongVisibleTags((prevTags) => [...prevTags, tag]);
				}
			});
		} else {
			setVisibleTags(data);
			maxTagLimit = data;
		}
		// apply only for the contacts page tags column show only 2 tags
		if (maxTagLimit?.length > 2 && smallTagGroup) {
			maxTagLimit?.forEach((tag, i) =>
				i < 2
					? setVisibleTags((prevTags) => [...prevTags, tag])
					: (setHiddenTags((prevTags) => [...prevTags, tag]),
					setLongVisibleTags((prevTags) => [...prevTags, tag])),
			);
		} else {
			setVisibleTags(maxTagLimit);
		}

		return () => {
			setVisibleTags([]);
			setHiddenTags([]);
			setLongVisibleTags([]);
		};
	}, [data, allowHiddenTags, maxVisibleTagWidth, smallTagGroup]);

	useEffect(() => {
		if (!visibleTags?.length && smallTagGroup) {
			if (longVisibleTags?.length) {
				setVisibleTags([longVisibleTags[0]]);
				setHiddenTags((prevTags) => prevTags?.filter((tag) => tag?.tagName !== longVisibleTags[0]?.tagName));
			}
		}
	}, [longVisibleTags, smallTagGroup, visibleTags?.length]);

	return (
		<div>
			<div className="bay-contact-tags-badges mt-0 gap-2">
				{visibleTags?.length > 0 ? (
					<>
						{visibleTags.map((tag, index) => (
							<div
								key={index}
								className={`bay-contact-tag-badge ${tag.style ? `${tag.style} text-white` : ''}`}
							>
								<BayTooltip
									disabled={
										smallTagGroup && tag?.tagName?.length < maxVisibleTagWidth
									}
									overlay={tag?.tagName}
									placement="top"
									overlayClassName=""
									inverseStyle={false}
								>
									<div
										className={`${smallTagGroup && tag?.tagName?.length > maxVisibleTagWidth ? 'text-truncate tag-width-limit cursor-pointer' : ''}`}
									>
										{tag?.tagName}
										{removable && (
											<IoCloseOutline
												className="ms-1 cursor-pointer"
												size={14}
												onClick={() => onDelete && onDelete(tag)}
											/>
										)}
									</div>
								</BayTooltip>
							</div>
						))}
						{visibleTags?.length <= 2 &&
							!hiddenTags?.length &&
							smallTagGroup &&
							!readOnly &&
							getAddTagsButton()}
					</>
				) : (
					contactsTooltip && !readOnly && getAddTagsButton(true)
				)}
				{contactsTooltip
					? allowHiddenTags &&
						hiddenTags.length > 0 && (
						<>
							<div
								data-button-id="hiddenTagsButton"
								className={`bay-contact-tag-badge hidden-tag-badge position-relative ${contactsTooltip ? 'hidden-tags-length bg-blue-500' : 'bg-info'} me-2 bg-blue text-white cursor-pointer`}
								style={{ width: hiddenTags.length > 3 ? '40px' : '32px' }}
								onClick={handleHiddenTagsClick}
							>
								+{hiddenTags?.length}
								{isModalVisible && (
									<div
										className={`custom-tags-modal ${isLastColumn ? 'last-column' : ''} ${hiddenTags?.length >= 10 ? 'more-hidden-tags' : ''}`}
										style={{
											width: `${calculateModalWidth()}px`,
											bottom: hiddenTags?.length >= 10 ? '0px' : '10px',
											left: isLastColumn && hiddenTags?.length >= 6 ? '20px' : '12px',
										}}
									>
										<div ref={modalRef} className="custom-modal-content w-100">
											<div className="tags-modal-header ms-1">
												<div className="d-flex tags-modal-title">{`All Tags for ${contactName}`}</div>
												<XLg
													className="cursor-pointer"
													fill="var(--bs-gray-700)"
													size={16}
													onClick={() => setIsModalVisible(!isModalVisible)}
												/>
											</div>
											<hr className="hidden-tags-modal-line" />
											<div
												className="bay-contact-tags-badges hidden-tags-badges my-1 ps-5 mt-3 gap-2"
												style={{
													width: `${calculateModalWidth()}px`,
													height: hiddenTags.length > 25 ? '275px' : '',
													overflowY:
														hiddenTags.length > 25 ? 'scroll' : 'hidden',
												}}
											>
												{hiddenTags?.map((tag, index) => (
													<div
														key={index}
														className={`bay-contact-tag-badge ${tag?.style ? `${tag?.style} text-white` : ''}`}
													>
														<BayTooltip
															disabled={
																smallTagGroup &&
																tag?.tagName?.length < maxVisibleTagWidth
															}
															overlay={tag?.tagName}
															placement="top"
															overlayClassName=""
															inverseStyle={false}
														>
															<div
																className={`${smallTagGroup && tag?.tagName?.length > maxVisibleTagWidth ? 'text-truncate tag-width-limit cursor-pointer' : ''}`}
															>
																{tag?.tagName}
															</div>
														</BayTooltip>
													</div>
												))}
											</div>
											{!readOnly && publicid && ownerpublicid && tagContactId && (
												<div
													className="cursor-pointer ms-1 hidden-add-tags-btn mb-3"
													onClick={() => {
														setIsLoadingTags && setIsLoadingTags(true);
														setContactPublicId && setContactPublicId(publicid);
														setContactPublicInfo &&
															setContactPublicInfo({ publicid, ownerpublicid });
														setTagsContactId && setTagsContactId(tagContactId);
														setEnableAddTagsModal && setEnableAddTagsModal(!enableAddTagsModal);
														setIsModalVisible(false);
													}}
												>
													<PlusLg fill="var(--bs-blue)" size={16} /> Add Tags
												</div>
											)}
										</div>
									</div>
								)}
							</div>
						</>
					)
					: allowHiddenTags &&
						hiddenTags.length > 0 && (
						<BayTooltip
							overlay={
								<div className="bay-contact-tags-badges my-1 gap-2">
									{hiddenTags?.map((tag, index) => {
										return (
											<div
												key={index}
												className={`bay-contact-tag-badge ${tag.style ? `${tag.style} text-white` : ''}`}
											>
												{tag.tagName}
											</div>
										);
									})}
								</div>
							}
							placement="top"
							overlayClassName=""
							inverseStyle={false}
						>
							<div className="bay-contact-tag-badge hidden-tag-badge me-2 bg-blue text-white cursor-pointer">
								+ {hiddenTags?.length}
							</div>
						</BayTooltip>
					)}
			</div>
		</div>
	);
};

export default BayTags;
